import React from "react"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgDigitsLogoNoBackground } from "@digits-shared/components/SVGIcons/digits/DigitsLogoNoBackground.svg"
import { SvgBarChartSquarePlus } from "@digits-shared/components/SVGIcons/line/BarChartSquarePlus.svg"
import { SvgElectrical01 } from "@digits-shared/components/SVGIcons/line/Electrical01.svg"
import { SvgUserPlus01 } from "@digits-shared/components/SVGIcons/line/UserPlus01.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import { WelcomeModal } from "@digits-shared/DesignSystem/Modals/WelcomeModal"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { JWTPermissionFlag } from "@digits-shared/session/jwt/jwtPermissions"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { ManagedModalName } from "src/frontend/components/Shared/ManagedModals/state/types"
import { useManagedModal } from "src/frontend/components/Shared/ManagedModals/useManagedModal"
import { useHasPermission } from "src/frontend/components/Shared/Permissions/Requires"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"
import { Experience } from "src/frontend/session/personas"

const ButtonGroup = styled.div`
  max-width: 348px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// Ideally, our digits logo accepts the gradient provided by WelcomeModal, but
// it doesn't work with the current SVG, so we're doing this as a quick-fix
const SolidColorDigitsLogo = styled(SvgDigitsLogoNoBackground)`
  ${svgIconStyles(colors.secondary)}
`

export const DigitsWelcomeModal: React.FC = () => {
  const generatePath = useFrontendPathGenerator()
  const canShow = useCanShowModal()

  const { dismissModal, isModalVisible } = useManagedModal(ManagedModalName.WelcomeToDigitsModal, {
    canShow,
  })

  const features = React.useMemo(
    () => [
      {
        title: "Track what you care about",
        message:
          "Assemble your custom dashboard with charts, lists, statements, and custom components.",
        icon: SvgBarChartSquarePlus,
      },
      {
        title: "Invite your team",
        message: "Collaborate with your team across the Digits platform, from Bills to Reports.",
        icon: SvgUserPlus01,
      },
      {
        title: "Add connections to power Digits",
        message: "Connect your accounts to utilize the power of your personal AI agents.",
        icon: SvgElectrical01,
      },
    ],
    []
  )

  if (!isModalVisible) return null

  return (
    <WelcomeModal title="Welcome to Digits!" icon={SolidColorDigitsLogo} features={features}>
      <ButtonGroup>
        <DigitsButton $fullWidth onClick={dismissModal}>
          Start Exploring
        </DigitsButton>
        <DigitsLinkButton
          $fullWidth
          to={generatePath(routes.allConnections)}
          onClick={dismissModal}
          $variant="secondary-dark"
        >
          Add a Connection
        </DigitsLinkButton>
      </ButtonGroup>
    </WelcomeModal>
  )
}

/**
 * NOTE: This modal should not show for PLG Self Serve Operators as they are already shown a different welcome modal
 * Effectively not including Experience.OperatorPortal excludes them from seeing this modal. This modal is focused on
 * creating connections and will not be shown to anyone without the `Create` flag on the `Connections` module.
 */
const useCanShowModal = () => {
  const { hasAccessToAspect, currentPrimaryExperience, isDoppelganger } =
    useSession<FrontendSession>()
  const canCreateConnection = useHasPermission({
    source: FrontendPermissionSource.LegalEntity,
    module: FrontendPermissionModule.Connections,
    flag: JWTPermissionFlag.Create,
  })

  const isClientPortal = currentPrimaryExperience === Experience.ClientPortal
  const isAccountantPortal = currentPrimaryExperience === Experience.AccountantPortal
  const canShowToPersona = isClientPortal || isAccountantPortal

  const hasConnectionsAspect = hasAccessToAspect(AspectCode.Connections)
  const hasAIBAspect = hasAccessToAspect(AspectCode.AIBookkeeper)

  return (
    !isDoppelganger &&
    canCreateConnection &&
    canShowToPersona &&
    hasConnectionsAspect &&
    hasAIBAspect
  )
}
